import React from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button, Input } from '@wove/web-ui'
import { X, Plus, Settings } from 'lucide-react'
import { CreateIntegrationRequest } from '@wove/api'

const addIntegrationSchema = z
  .object({
    integrationType: z
      .string()
      .min(1, 'Integration type is required')
      .max(50, 'Integration type must be 50 characters or less'),
    integrationSource: z.string().min(1, 'Integration source is required'),
    includeSettings: z.boolean().default(false),
    cargoWiseSettings: z
      .object({
        credentials: z.object({
          username: z.string().min(1, 'Username is required'),
          password: z.string().min(1, 'Password is required'),
          eAdaptorUrl: z.string().url('Must be a valid URL'),
        }),
      })
      .optional(),
  })
  .refine(
    (data) => {
      // If includeSettings is true and source is CargoWise, require settings
      if (data.includeSettings && data.integrationSource === 'cw') {
        return !!data.cargoWiseSettings
      }
      return true
    },
    {
      message: 'CargoWise settings are required when include settings is enabled',
      path: ['cargoWiseSettings'],
    },
  )

type FormData = z.infer<typeof addIntegrationSchema>

interface AddIntegrationFormProps {
  onSubmit: (
    data: CreateIntegrationRequest & { includeSettings?: boolean; cargoWiseSettings?: any },
  ) => void
  onCancel: () => void
  isSubmitting: boolean
}

const INTEGRATION_SOURCES = [
  { value: 'cw', label: 'CargoWise', description: 'Connect to CargoWise eAdaptor' },
  { value: 'freight', label: 'FreightOS', description: 'Connect to FreightOS API' },
  { value: 'sap', label: 'SAP', description: 'Connect to SAP system' },
  { value: 'custom', label: 'Custom', description: 'Custom integration' },
]

export default function AddIntegrationForm({
  onSubmit,
  onCancel,
  isSubmitting,
}: AddIntegrationFormProps) {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(addIntegrationSchema),
    defaultValues: {
      integrationType: 'tms',
      integrationSource: 'cw',
      includeSettings: false,
    },
  })

  const watchedValues = watch(['integrationSource', 'includeSettings'])
  const [integrationSource, includeSettings] = watchedValues

  const onFormSubmit = (data: FormData) => {
    const submitData: CreateIntegrationRequest & {
      includeSettings?: boolean
      cargoWiseSettings?: any
    } = {
      integrationType: data.integrationType,
      integrationSource: data.integrationSource,
      metadata: {},
    }

    if (data.includeSettings) {
      submitData.includeSettings = true
      if (data.integrationSource === 'cw' && data.cargoWiseSettings) {
        submitData.cargoWiseSettings = data.cargoWiseSettings
        submitData.metadata = {
          cargowise: data.cargoWiseSettings,
        }
      }
    }

    onSubmit(submitData)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full m-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Plus className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Add New Integration</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onCancel} disabled={isSubmitting}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6 space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Integration Setup</h4>
            <p className="text-sm text-blue-700">
              Choose the type of integration you want to add. You can configure connection settings
              now or set them up later from the integration detail page.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Integration Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Integration Type *
              </label>
              <Controller
                name="integrationType"
                control={control}
                render={({ field }) => (
                  <select
                    {...field}
                    disabled={isSubmitting}
                    className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.integrationType ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="tms">TMS (Transportation Management)</option>
                    <option value="wms">WMS (Warehouse Management)</option>
                    <option value="erp">ERP (Enterprise Resource Planning)</option>
                    <option value="api">API Integration</option>
                  </select>
                )}
              />
              {errors.integrationType && (
                <p className="mt-1 text-sm text-red-600">{errors.integrationType.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                The category of system you're integrating with
              </p>
            </div>

            {/* Integration Source */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Integration Source *
              </label>
              <Controller
                name="integrationSource"
                control={control}
                render={({ field }) => (
                  <select
                    {...field}
                    disabled={isSubmitting}
                    className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.integrationSource ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    {INTEGRATION_SOURCES.map((source) => (
                      <option key={source.value} value={source.value}>
                        {source.label}
                      </option>
                    ))}
                  </select>
                )}
              />
              {errors.integrationSource && (
                <p className="mt-1 text-sm text-red-600">{errors.integrationSource.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                {INTEGRATION_SOURCES.find((s) => s.value === integrationSource)?.description}
              </p>
            </div>
          </div>

          {/* Include Settings Option */}
          <div className="border-t border-gray-200 pt-6">
            <div className="flex items-center">
              <Controller
                name="includeSettings"
                control={control}
                render={({ field }) => (
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={field.onChange}
                      disabled={isSubmitting}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Configure connection settings now
                    </span>
                  </label>
                )}
              />
            </div>
            <p className="mt-1 text-xs text-gray-500 ml-6">
              You can also configure these settings later from the integration detail page
            </p>
          </div>

          {/* CargoWise Settings (conditional) */}
          {includeSettings && integrationSource === 'cw' && (
            <div className="border border-gray-200 rounded-lg p-4 space-y-4">
              <div className="flex items-center">
                <Settings className="h-5 w-5 text-blue-600 mr-2" />
                <h4 className="text-sm font-medium text-gray-900">CargoWise Connection Settings</h4>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Username */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
                  <Controller
                    name="cargoWiseSettings.credentials.username"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        disabled={isSubmitting}
                        placeholder="e.g., CargoWiseUser"
                        className={
                          errors.cargoWiseSettings?.credentials?.username ? 'border-red-300' : ''
                        }
                      />
                    )}
                  />
                  {errors.cargoWiseSettings?.credentials?.username && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.cargoWiseSettings.credentials.username.message}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                  <Controller
                    name="cargoWiseSettings.credentials.password"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="password"
                        disabled={isSubmitting}
                        placeholder="••••••••••••"
                        className={
                          errors.cargoWiseSettings?.credentials?.password ? 'border-red-300' : ''
                        }
                      />
                    )}
                  />
                  {errors.cargoWiseSettings?.credentials?.password && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.cargoWiseSettings.credentials.password.message}
                    </p>
                  )}
                </div>
              </div>

              {/* eAdaptor URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  eAdaptor URL *
                </label>
                <Controller
                  name="cargoWiseSettings.credentials.eAdaptorUrl"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      disabled={isSubmitting}
                      placeholder="https://CW1services.wisegrid.net/eAdaptor"
                      className={
                        errors.cargoWiseSettings?.credentials?.eAdaptorUrl ? 'border-red-300' : ''
                      }
                    />
                  )}
                />
                {errors.cargoWiseSettings?.credentials?.eAdaptorUrl && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.cargoWiseSettings.credentials.eAdaptorUrl.message}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Integration'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
